import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Mail,
  Calendar,
  Clock,
  RefreshCw,
  Combine,
  Download,
  FileText,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import axios from '../../../utils/axios';
import RichTextViewer from '../../common/RichTextViewer';
import { showToast } from '../../../utils/toast';
import { formatDate, formatStatus, getStatusClass, handleDownloadFile } from '../../../utils/utils.global';
import SendReminderModal, { isReminderEligible } from './SendReminderModal';
import InvitationDetailsModal from './InvitationDetailsModal';

const SubmissionDetailsModal = ({
  submission,
  onClose,
  onTakeAction,
  onArchiveClick,
  onCombineFiles,
  combiningFiles,
  combineProgress,
  combineResult,
  onResendInvitation,
  onCancelInvitation,
  onViewReminders,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [viewing, setViewing] = useState(submission);
  const [showModalActionsDropdown, setShowModalActionsDropdown] = useState(false);

  // Section loading flags
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [loadingAuthors, setLoadingAuthors] = useState(false);
  const [loadingReviewers, setLoadingReviewers] = useState(false);
  const [loadingEditors, setLoadingEditors] = useState(false);
  const [loadingSuggested, setLoadingSuggested] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [loadingPrevious, setLoadingPrevious] = useState(false);

  // Section data
  const [keywords, setKeywords] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [reviewerInvitations, setReviewerInvitations] = useState([]);
  const [editorInvitations, setEditorInvitations] = useState([]);
  const [suggestedReviewers, setSuggestedReviewers] = useState([]);
  const [submittedReviews, setSubmittedReviews] = useState([]);
  const [previousSubmissions, setPreviousSubmissions] = useState([]);

  // Invitation / reminder modals
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [reminderInvitation, setReminderInvitation] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);

  const modalContentRef = useRef(null);
  const actionButtonRef = useRef(null);
  const dropdownRef = useRef(null);

  const getSubmissionId = (sub) => sub?.revision_id || sub?.id || sub?.article_id;

  // Adopt outer submission when it changes.
  useEffect(() => {
    setViewing(submission);
  }, [submission]);

  // ---------------------------------------------------------------------------
  // Data fetchers
  // ---------------------------------------------------------------------------

  const fetchKeywords = useCallback(async (sub) => {
    if (!sub) return;
    setLoadingKeywords(true);
    try {
      const response = await axios.post('/editors/getKeywords', {
        article_id: getSubmissionId(sub),
      });
      setKeywords(
        response.data.success === 'gotKeywords'
          ? response.data.keywords.map((item) => item.keyword)
          : []
      );
    } catch (error) {
      console.error('Error fetching keywords:', error);
      setKeywords([]);
    } finally {
      setLoadingKeywords(false);
    }
  }, []);

  const fetchSuggestedReviewers = useCallback(async (sub) => {
    if (!sub) return;
    setLoadingSuggested(true);
    try {
      const response = await axios.get(
        `/editors/getSuggestedReviewers?article_id=${getSubmissionId(sub)}`
      );
      setSuggestedReviewers(
        response.data.success === 'Reviewers Available'
          ? response.data.suggestedReviewers || []
          : []
      );
    } catch (error) {
      console.error('Error fetching suggested reviewers:', error);
      setSuggestedReviewers([]);
    } finally {
      setLoadingSuggested(false);
    }
  }, []);

  const fetchSubmittedReviews = useCallback(async (sub) => {
    if (!sub) return;
    setLoadingReviews(true);
    try {
      const response = await axios.get('/editors/getReviews', {
        params: { articleID: getSubmissionId(sub) },
      });
      setSubmittedReviews(
        response.data.success === 'Review Available'
          ? response.data.reviews || []
          : []
      );
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setSubmittedReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  }, []);

  const fetchAuthors = useCallback(async (sub) => {
    if (!sub) return;
    setLoadingAuthors(true);
    try {
      const response = await axios.get('/editors/getAuthors', {
        params: { articleID: getSubmissionId(sub) },
      });
      setAuthors(
        response.data.success === 'Authors Available'
          ? response.data.authorsList || []
          : []
      );
    } catch (error) {
      console.error('Error fetching authors:', error);
      setAuthors([]);
    } finally {
      setLoadingAuthors(false);
    }
  }, []);

  const fetchReviewerInvitations = useCallback(async (sub) => {
    if (!sub) return;
    setLoadingReviewers(true);
    try {
      const response = await axios.post('/editors/articleinvitations?type=review', {
        article_id: getSubmissionId(sub),
      });
      setReviewerInvitations(
        response.data.success === 'Review Available'
          ? response.data.reviews || []
          : []
      );
    } catch (error) {
      console.error('Error fetching reviewer invitations:', error);
      setReviewerInvitations([]);
    } finally {
      setLoadingReviewers(false);
    }
  }, []);

  const fetchEditorInvitations = useCallback(async (sub) => {
    if (!sub) return;
    setLoadingEditors(true);
    try {
      const response = await axios.post('/editors/articleinvitations?type=edit', {
        article_id: getSubmissionId(sub),
      });
      setEditorInvitations(
        response.data.success === 'Review Available'
          ? response.data.reviews || []
          : []
      );
    } catch (error) {
      console.error('Error fetching editor invitations:', error);
      setEditorInvitations([]);
    } finally {
      setLoadingEditors(false);
    }
  }, []);

  const fetchPreviousSubmissions = useCallback(
    async (sub) => {
      if (!sub) return;
      setLoadingPrevious(true);

      try {
        const isAdmin =
          user?.editorialLevel === 'editor_in_chief' ||
          user?.editorialLevel === 'admin';

        const submissionId = getSubmissionId(sub);
        const endpoint = isAdmin
          ? `/editors/allPreviousSubmissions/${submissionId}`
          : `/editors/myPreviousSubmissions/${submissionId}`;

        const response = await axios.post(endpoint);

        const payload = response?.data || {};
        const list = Array.isArray(payload.submissions) ? payload.submissions : [];
        setPreviousSubmissions(list);
      } catch (error) {
        console.error('Error fetching Related Submissions:', error);
        setPreviousSubmissions([]);
      } finally {
        setLoadingPrevious(false);
      }
    },
    [user?.editorialLevel]
  );

  // Fetch every section whenever the viewed submission changes.
  useEffect(() => {
    if (!viewing) return;

    // Reset all section data first, then refetch.
    setKeywords([]);
    setSuggestedReviewers([]);
    setSubmittedReviews([]);
    setAuthors([]);
    setReviewerInvitations([]);
    setEditorInvitations([]);
    setPreviousSubmissions([]);

    fetchKeywords(viewing);
    fetchSuggestedReviewers(viewing);
    fetchSubmittedReviews(viewing);
    fetchAuthors(viewing);
    fetchReviewerInvitations(viewing);
    fetchEditorInvitations(viewing);
    fetchPreviousSubmissions(viewing);

    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [
    viewing,
    fetchKeywords,
    fetchSuggestedReviewers,
    fetchSubmittedReviews,
    fetchAuthors,
    fetchReviewerInvitations,
    fetchEditorInvitations,
    fetchPreviousSubmissions,
  ]);

  // Close "Take Action" dropdown on outside click.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        actionButtonRef.current &&
        !actionButtonRef.current.contains(event.target)
      ) {
        setShowModalActionsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleRefreshSection = (section) => {
    if (!viewing) return;

    const refreshers = {
      keywords: fetchKeywords,
      suggested: fetchSuggestedReviewers,
      reviews: fetchSubmittedReviews,
      authors: fetchAuthors,
      reviewers: fetchReviewerInvitations,
      editors: fetchEditorInvitations,
      previous: fetchPreviousSubmissions,
    };

    refreshers[section]?.(viewing);
  };

  const handleSelectSubmission = (sub) => {
    setShowModalActionsDropdown(false);
    if (sub) setViewing(sub);
  };

  const handleModalAction = (action) => {
    const submissionId = getSubmissionId(viewing);
    setShowModalActionsDropdown(false);

    if (onTakeAction) {
      onTakeAction(action, submissionId);
      return;
    }

    const routes = {
      accept: `/editors/process/accept?a=${submissionId}`,
      reject: `/editors/process/reject?a=${submissionId}`,
      return: `/editors/process/return?a=${submissionId}`,
      revise: `/editors/process/revise?a=${submissionId}`,
      'invite-reviewer': `/editors/process/invite?reviewer=true&a=${submissionId}`,
      'invite-editor': `/editors/process/invite?editor=true&a=${submissionId}`,
    };

    if (routes[action]) {
      navigate(routes[action]);
    } else {
      showToast(`${action} action triggered for submission`, 'info');
    }

    onClose();
  };

  const handleOpenInvitation = (invitation) => {
    setSelectedInvitation(invitation);
    setShowInvitationModal(true);
  };

  const handleOpenReminder = (invitation) => {
    setReminderInvitation(invitation);
    setShowReminderModal(true);
  };

  const handleReminderSuccess = () => {
    // Refresh both invitation lists so reminder counts update immediately.
    fetchReviewerInvitations(viewing);
    fetchEditorInvitations(viewing);
  };

  if (!viewing) return null;

  const showArchiveButton = !!onArchiveClick;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Submission Details</h3>
              <p className="text-sm text-gray-500 mt-1">ID: {getSubmissionId(viewing)}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div ref={modalContentRef} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8">
            {/* Title */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Title</h4>
              <h2 className="text-xl font-bold text-gray-900">{viewing.title}</h2>
            </div>

            {/* Discipline / Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Discipline</h4>
                <p className="text-gray-900">{viewing.discipline || 'Not specified'}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Article Type</h4>
                <p className="text-gray-900">{viewing.type || 'Not specified'}</p>
              </div>
            </div>

            {/* Status + badges */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`status-text ${getStatusClass(viewing.status)}`}>
                {formatStatus(viewing.status)}
                {' '}Last updated:{' '}
                {viewing.updatedAt ? formatDate(viewing.updatedAt) : viewing.last_updated || 'N/A'}
              </span>
              {viewing.isWomenInScience && (
                <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-pink-100 text-pink-800">
                  Women in Contemporary Science in Africa
                </span>
              )}
              {viewing.isBelispointAcademic && (
                <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  Belispoint Academic
                </span>
              )}
              {viewing.isKidnappingForRansom && (
                <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                  Kidnapping For Ransom
                </span>
              )}
            </div>

            {/* Corresponding Author */}
            <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Corresponding Author</h4>
              <p className="font-medium text-gray-900">
                {viewing.correspondingAuthor || 'Not specified'}
              </p>
              <p className="text-sm text-gray-600 flex items-center mt-1">
                <Mail className="w-4 h-4 text-gray-400 mr-2" />
                {viewing.correspondingEmail || 'Not provided'}
              </p>
            </div>

            {/* Abstract */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-sm font-medium text-gray-500 mb-4">Abstract</h4>
              <RichTextViewer content={viewing.abstract} />
            </div>

            {/* Keywords */}
            <SectionCard
              title="Keywords"
              loading={loadingKeywords}
              onRefresh={() => handleRefreshSection('keywords')}
            >
              {keywords.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-purple-50 text-[--asfi-purple] text-sm rounded-full"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              ) : (
                <EmptyState message="No keywords available" />
              )}
            </SectionCard>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-1">Submitted Date</h4>
                <p className="text-sm text-gray-900 flex items-center">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  {viewing.submittedDate ? formatDate(viewing.submittedDate) : 'N/A'}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-1">Last Updated</h4>
                <p className="text-sm text-gray-900 flex items-center">
                  <Clock className="w-4 h-4 text-gray-400 mr-2" />
                  {viewing.updatedAt ? formatDate(viewing.updatedAt) : viewing.date || 'N/A'}
                </p>
              </div>
            </div>

            {/* Files */}
            {viewing.files && Object.keys(viewing.files).length > 0 && (
              <FilesSection
                files={viewing.files}
                submissionId={getSubmissionId(viewing)}
                onCombineFiles={onCombineFiles}
                combiningFiles={combiningFiles}
                combineProgress={combineProgress}
                combineResult={combineResult}
              />
            )}

            {/* Authors */}
            <SectionCard
              title="Authors"
              loading={loadingAuthors}
              onRefresh={() => handleRefreshSection('authors')}
            >
              {authors.length > 0 ? <AuthorsTable authors={authors} /> : <EmptyState message="No author information available" />}
            </SectionCard>

            {/* Disclosures */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-sm font-medium text-gray-500 mb-4">Disclosures</h4>
              <ul className="space-y-3 text-sm text-gray-700 list-disc pl-5">
                <li>I confirm that the manuscript has been submitted solely to ASFIRJ and is not published, in press, or submitted elsewhere, with exception of submission to preprint servers.</li>
                <li>I am aware that ASFIRJ requires that all authors disclose all potential sources of conflict of interest in regarding the submitted manuscript and I confirm that all authors have done so.</li>
                <li>I confirm that the research that yielded the manuscript being submitted meets the ethical guidelines and adheres to all legal research requirements of the study country.</li>
                <li>I have prepared my manuscript and files, including text, tables, and figures, in accordance with ASFIRJ's style and formatting requirements as described at: <a href="https://asfirj.org/authors.html" target="_blank" rel="noopener noreferrer" className="text-[--asfi-blue] hover:underline">asfirj.org/authors.html</a>.</li>
                <li>I confirm that each of the co-authors acknowledges their participation in the research that yielded the manuscript being submitted and agrees to the submission of the manuscript to ASFIRJ.</li>
                <li>I confirm that the contributions each author made to the manuscript are specified in the authors' contribution section of the manuscript.</li>
                <li>I confirm that the manuscript being submitted and the data it contains are unpublished and original.</li>
                <li>I am willing to pay ASFIRJ's APC for the submitted manuscript if it is accepted for publication in the journal as indicated at <a href="https://asfirj.org/aboutus.html" target="_blank" rel="noopener noreferrer" className="text-[--asfi-blue] hover:underline">asfirj.org/aboutus.html</a>.</li>
              </ul>
            </div>

            {/* Suggested Reviewers */}
            <SectionCard
              title="Suggested Reviewers"
              loading={loadingSuggested}
              onRefresh={() => handleRefreshSection('suggested')}
            >
              {suggestedReviewers.length > 0 ? (
                <SuggestedReviewersTable reviewers={suggestedReviewers} />
              ) : (
                <EmptyState message="No suggested reviewers" />
              )}
            </SectionCard>

            {/* Submitted Reviews */}
            <SectionCard
              title="Submitted Reviews"
              loading={loadingReviews}
              onRefresh={() => handleRefreshSection('reviews')}
            >
              {submittedReviews.length > 0 ? (
                <SubmittedReviewsTable
                  reviews={submittedReviews}
                  submissionId={getSubmissionId(viewing)}
                  navigate={navigate}
                />
              ) : (
                <EmptyState message="No reviews have been submitted" />
              )}
            </SectionCard>

            {/* Reviewer Invitations */}
            <SectionCard
              title="Reviewer Invitations"
              loading={loadingReviewers}
              onRefresh={() => handleRefreshSection('reviewers')}
            >
              {reviewerInvitations.length > 0 ? (
                <InvitationsTable
                  invitations={reviewerInvitations}
                  roleLabel="Reviewer"
                  onView={handleOpenInvitation}
                  onRemind={handleOpenReminder}
                />
              ) : (
                <EmptyState message="No review invitations have been sent" />
              )}
            </SectionCard>

            {/* Editor Invitations */}
            <SectionCard
              title="Editor Invitations"
              loading={loadingEditors}
              onRefresh={() => handleRefreshSection('editors')}
            >
              {editorInvitations.length > 0 ? (
                <InvitationsTable
                  invitations={editorInvitations}
                  roleLabel="Editor"
                  onView={handleOpenInvitation}
                  onRemind={handleOpenReminder}
                />
              ) : (
                <EmptyState message="No editor invitations have been sent" />
              )}
            </SectionCard>

            {/* Related Submissions */}
            <SectionCard
              title="Related Submissions"
              loading={loadingPrevious}
              onRefresh={() => handleRefreshSection('previous')}
            >
              {previousSubmissions.length > 0 ? (
                <RelatedSubmissionsTable
                  submissions={previousSubmissions}
                  currentSubmissionId={getSubmissionId(viewing)}
                  showArchiveButton={showArchiveButton}
                  onSelect={handleSelectSubmission}
                  onArchive={onArchiveClick}
                />
              ) : (
                <EmptyState message="No Related Submissions found" />
              )}
            </SectionCard>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>

            <div className="relative" ref={dropdownRef}>
              <button
                ref={actionButtonRef}
                onClick={() => setShowModalActionsDropdown(!showModalActionsDropdown)}
                className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2"
              >
                Take Action
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showModalActionsDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {showModalActionsDropdown && (
                <div
                  className="absolute bottom-full mb-2 right-0 w-56 bg-white rounded-lg shadow-xl py-1 z-[60] border border-gray-200"
                  style={{ maxHeight: '300px', overflowY: 'auto' }}
                >
                  <DropdownItem onClick={() => handleModalAction('return')}>
                    Return For Correction
                  </DropdownItem>
                  <DropdownItem onClick={() => handleModalAction('revise')}>
                    Revise
                  </DropdownItem>
                  <DropdownItem onClick={() => handleModalAction('invite-reviewer')}>
                    Invite Reviewer
                  </DropdownItem>
                  <DropdownItem onClick={() => handleModalAction('invite-editor')}>
                    Invite Editor
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => handleModalAction('accept')}
                    className="text-green-600 hover:bg-green-50 border-b border-gray-100"
                  >
                    Accept
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => handleModalAction('reject')}
                    className="text-red-600 hover:bg-red-50"
                  >
                    Reject
                  </DropdownItem>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invitation Details Modal */}
      {showInvitationModal && selectedInvitation && (
        <InvitationDetailsModal
          invitation={selectedInvitation}
          onClose={() => {
            setShowInvitationModal(false);
            setSelectedInvitation(null);
          }}
          onSendReminder={(inv) => {
            setShowInvitationModal(false);
            setSelectedInvitation(null);
            setReminderInvitation(inv);
            setShowReminderModal(true);
          }}
          onResend={onResendInvitation}
          onCancel={onCancelInvitation}
          onViewReminders={onViewReminders}
        />
      )}

      {/* Reminder Modal */}
      {showReminderModal && reminderInvitation && (
        <SendReminderModal
          invitations={[reminderInvitation]}
          onClose={() => {
            setShowReminderModal(false);
            setReminderInvitation(null);
          }}
          onSuccess={handleReminderSuccess}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small presentational helpers — extracted so the main JSX stays readable.
// ---------------------------------------------------------------------------

const SectionCard = ({ title, loading, onRefresh, children }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <div className="flex justify-between items-center mb-4">
      <h4 className="text-sm font-medium text-gray-500">{title}</h4>
      <button
        onClick={onRefresh}
        className="p-1 text-gray-400 hover:text-[--asfi-purple] transition-colors"
        title={`Refresh ${title.toLowerCase()}`}
      >
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
    {loading ? (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-solid border-purple-600 border-r-transparent" />
      </div>
    ) : (
      children
    )}
  </div>
);

const EmptyState = ({ message }) => (
  <p className="text-sm text-gray-500 text-center py-4">{message}</p>
);

const DropdownItem = ({ onClick, children, className = 'text-gray-700 hover:bg-gray-50 border-b border-gray-100' }) => (
  <button
    onClick={onClick}
    className={`block w-full text-left px-4 py-2.5 text-sm ${className}`}
  >
    {children}
  </button>
);

const InvitationsTable = ({ invitations, roleLabel, onView, onRemind }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{roleLabel}</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date Invited</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {invitations.map((invitation) => {
          const status = invitation.invitation_status || invitation.status || 'invite_sent';
          const name =
            invitation.reviewer_name ||
            invitation.editor_name ||
            invitation.invited_user ||
            'Unknown';
          const email =
            invitation.invited_user ||
            invitation.reviewer_email ||
            invitation.editor_email ||
            '';
          const isOverdue =
            invitation.dueDate && new Date(invitation.dueDate) < new Date();

          return (
            <tr key={invitation.id} className="hover:bg-gray-50">
              <td className="px-4 py-2">
                <p className="text-sm font-medium text-gray-900">{name}</p>
                <p className="text-xs text-gray-500">{email}</p>
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {formatDate(
                  invitation.invitation_date ||
                    invitation.date_invited ||
                    invitation.created_at
                )}
              </td>
              <td className="px-4 py-2">
                <span className={`status-text ${getStatusClass(status)}`}>
                  {formatStatus(status)}
                </span>
                {isOverdue && status === 'invite_sent' && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                    Overdue
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-right">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onView(invitation)}
                    className="text-sm text-[--asfi-purple] hover:text-[--asfi-blue] font-medium"
                  >
                    View
                  </button>
                  {isReminderEligible(invitation) && (
                    <button
                      onClick={() => onRemind(invitation)}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                      title="Send Reminder"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const AuthorsTable = ({ authors }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {['Name', 'ORCID-ID', 'Affiliation', 'City', 'Country', 'Email', 'ASFI Membership ID'].map(
            (h) => (
              <th
                key={h}
                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {h}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {authors.map((author) => {
          const fullName = [author.prefix, author.firstname, author.lastname]
            .filter(Boolean)
            .join(' ')
            .trim();

          return (
            <tr key={author.id} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-sm text-gray-900">
                {fullName || author.authors_fullname || 'Unknown'}
              </td>
              <td className="px-4 py-2 text-sm text-[--asfi-blue]">
                {author.orcid_id ? (
                  <a
                    href={author.orcid_id}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {author.orcid_id}
                  </a>
                ) : (
                  'N/A'
                )}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {author.affiliation || author.affiliations || 'N/A'}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {author.city || author.affiliation_city || 'N/A'}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {author.country || author.affiliation_country || 'N/A'}
              </td>
              <td className="px-4 py-2 text-sm text-[--asfi-purple]">
                {author.authors_email ? (
                  <a href={`mailto:${author.authors_email}`} className="hover:underline">
                    {author.authors_email}
                  </a>
                ) : (
                  'N/A'
                )}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {author.asfi_membership_id || 'N/A'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const SuggestedReviewersTable = ({ reviewers }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {['Reviewer', 'Affiliation', 'Country', 'City'].map((h) => (
            <th
              key={h}
              className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {reviewers.map((reviewer) => {
          const name =
            reviewer.fullname ||
            [reviewer.firstname, reviewer.lastname].filter(Boolean).join(' ').trim() ||
            'Unknown';
          return (
            <tr key={reviewer.id} className="hover:bg-gray-50">
              <td className="px-4 py-2">
                <p className="text-sm font-medium text-gray-900">{name}</p>
                {reviewer.email && <p className="text-xs text-gray-500">{reviewer.email}</p>}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">{reviewer.affiliation || 'N/A'}</td>
              <td className="px-4 py-2 text-sm text-gray-600">{reviewer.affiliation_country || 'N/A'}</td>
              <td className="px-4 py-2 text-sm text-gray-600">{reviewer.affiliation_city || 'N/A'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const SubmittedReviewsTable = ({ reviews, submissionId, navigate }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {['Reviewer', 'Date Submitted', 'Recommendation', 'Status', ''].map((h, i) => (
            <th
              key={i}
              className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase ${
                h === '' ? 'text-right' : 'text-left'
              }`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {reviews.map((review) => (
          <tr key={review.id} className="hover:bg-gray-50">
            <td className="px-4 py-2">
              <p className="text-sm font-medium text-gray-900">
                {review.reviewer_name || 'Anonymous'}
              </p>
              {review.reviewer_email && (
                <p className="text-xs text-gray-500">{review.reviewer_email}</p>
              )}
            </td>
            <td className="px-4 py-2 text-sm text-gray-600">
              {formatDate(review.date_completed || review.date_created)}
            </td>
            <td className="px-4 py-2 text-sm text-gray-600">
              {review.overall_recommendation || 'N/A'}
            </td>
            <td className="px-4 py-2">
              <span className={`status-text ${getStatusClass(review.review_status || 'submitted')}`}>
                {formatStatus(review.review_status || 'submitted')}
              </span>
            </td>
            <td className="px-4 py-2 text-right">
              <button
                onClick={() =>
                  navigate(`/editors/review/content/${review.review_id}/${submissionId}`)
                }
                className="text-sm text-[--asfi-purple] hover:text-[--asfi-blue] font-medium"
              >
                View Review
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const RelatedSubmissionsTable = ({
  submissions,
  currentSubmissionId,
  showArchiveButton,
  onSelect,
  onArchive,
}) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {[
            'Submission',
            'Date',
            'Status',
            'Reviewer Invitations',
            'Editor Invitations',
            '',
          ].map((h, i) => (
            <th
              key={i}
              className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase ${
                h === '' ? 'text-right' : 'text-left'
              }`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {submissions.map((sub, index) => {
          const submissionDate = sub.submittedDate || sub.date_submitted;
          const formattedDate = submissionDate ? formatDate(submissionDate) : 'N/A';
          const articleType = sub.type || sub.article_type || 'Not specified';
          const isCurrentVersion = sub.revision_id === currentSubmissionId;

          return (
            <tr
              key={sub.revision_id || index}
              className={`hover:bg-gray-50 transition-colors ${
                isCurrentVersion ? 'bg-green-50' : ''
              }`}
            >
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-gray-900">{sub.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-[--asfi-purple]">
                    {sub.revision_id || sub.id}
                  </p>
                  {isCurrentVersion && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Current Version
                    </span>
                  )}
                </div>
                {sub.isWomenInScience && (
                  <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-pink-100 text-pink-800">
                    Women in Contemporary Science in Africa
                  </span>
                )}
                {sub.isBelispointAcademic && (
                  <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    Belispoint Academic
                  </span>
                )}
                {sub.isKidnappingForRansom && (
                  <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-red-100 text-red-800">
                    Kidnapping For Ransom
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <p className="text-sm text-gray-900">{formattedDate}</p>
                <p className="text-xs text-gray-500 mt-1">{articleType}</p>
              </td>
              <td className="px-4 py-3">
                <span className={`status-text ${getStatusClass(sub.status)}`}>
                  {formatStatus(sub.status)}
                </span>
              </td>
              <td className="px-4 py-3">
                <ul className="text-xs space-y-1">
                  <li className="text-green-600">
                    Accepted: {sub.reviewerInvitations?.accepted || 0}
                  </li>
                  <li className="text-red-600">
                    Declined: {sub.reviewerInvitations?.declined || 0}
                  </li>
                  <li className="text-yellow-600">
                    Pending: {sub.reviewerInvitations?.pending || 0}
                  </li>
                </ul>
              </td>
              <td className="px-4 py-3">
                <ul className="text-xs space-y-1">
                  <li className="text-green-600">
                    Accepted: {sub.editorInvitations?.accepted || 0}
                  </li>
                  <li className="text-red-600">
                    Declined: {sub.editorInvitations?.declined || 0}
                  </li>
                  <li className="text-yellow-600">
                    Pending: {sub.editorInvitations?.pending || 0}
                  </li>
                </ul>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onSelect(sub)}
                    className="text-sm text-[--asfi-purple] hover:text-[--asfi-blue] font-medium"
                    title="View this version"
                  >
                    View
                  </button>
                  {!isCurrentVersion && showArchiveButton && (
                    <button
                      onClick={() => onArchive(sub.revision_id || sub.id)}
                      className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                    >
                      Archive
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {submissions.length > 1 && (
      <div className="mt-4 text-xs text-gray-500 text-right">
        Showing {submissions.length} versions (including current)
      </div>
    )}
  </div>
);

const FilesSection = ({
  files,
  submissionId,
  onCombineFiles,
  combiningFiles,
  combineProgress,
  combineResult,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <div className="flex justify-between items-center mb-4">
      <h4 className="text-sm font-medium text-gray-500">Files</h4>
      {onCombineFiles ? (
        <button
          className={`px-3 py-1 text-white text-sm rounded-lg transition-colors flex items-center gap-1 ${
            combiningFiles ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'
          }`}
          onClick={() => onCombineFiles(submissionId)}
          disabled={combiningFiles}
        >
          {combiningFiles ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Combining... {combineProgress > 0 && `${combineProgress}%`}
            </>
          ) : (
            <>
              <Combine className="w-4 h-4" />
              Combine Files
            </>
          )}
        </button>
      ) : (
        <button className="px-3 py-1 bg-[--asfi-purple] text-white text-sm rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-1">
          <Combine className="w-4 h-4" />
          Combine Files
        </button>
      )}
    </div>

    {onCombineFiles && combiningFiles && combineProgress > 0 && (
      <div className="mb-4">
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${combineProgress}%` }}
          />
        </div>
      </div>
    )}

    {onCombineFiles && combineResult?.combinedFile && (
      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-700 mb-2">✓ Files combined successfully!</p>
        <a
          href={combineResult.combinedFile}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
        >
          <Download className="w-4 h-4" />
          Download Combined PDF
        </a>
      </div>
    )}

    <div className="space-y-3">
      {files.manuscript && (
        <FileRow label="Manuscript File" value={files.manuscript} downloadAs="Manuscript" />
      )}
      {files.cover_letter && (
        <FileRow label="Cover Letter" value={files.cover_letter} downloadAs="Cover Letter" />
      )}

      <div className="mt-3">
        <p className="text-sm font-medium text-gray-700 mb-2">
          Additional Document Files (supplementary materials, tables, figures, graphic abstract):
        </p>
        <div className="space-y-2">
          {Object.entries(files)
            .filter(([key]) => !['manuscript', 'cover_letter'].includes(key))
            .map(([key, value]) => (
              <FileRow
                key={key}
                label={key.replace(/_/g, ' ')}
                value={value}
                downloadAs={key}
                capitalizeLabel
                compact
              />
            ))}
        </div>
      </div>
    </div>

    <p className="text-xs text-gray-500 mt-3">
      Please note that this only applies to papers submitted before 2025
    </p>
  </div>
);

const FileRow = ({ label, value, downloadAs, capitalizeLabel, compact }) => (
  <div
    className={`flex items-center justify-between ${
      compact ? 'p-2' : 'p-3'
    } bg-gray-50 rounded-lg`}
  >
    <div className="flex items-center min-w-0">
      <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
      <span className={`text-sm text-gray-700 ${capitalizeLabel ? 'capitalize' : ''}`}>
        {label}:
      </span>
      <span className="ml-2 text-sm text-gray-600 truncate">{value}</span>
    </div>
    <button
      className="p-1 text-gray-500 hover:text-[--asfi-purple] hover:bg-white rounded transition-colors flex-shrink-0"
      onClick={() => handleDownloadFile(value, downloadAs)}
      title={`Download ${label}`}
    >
      <Download className="w-4 h-4" />
    </button>
  </div>
);

export default SubmissionDetailsModal;